import axios from 'axios';

import { BACKEND_URL, GET_CRS_DATA } from './apiConfig';
import { STATUS_CONFIRMED } from './utils';

let REFRESH_ATTEMPS_NUMBER = 5;
const REFRESH_FAILED_TIME_RETRY = 60; // seconds

const baseUrl = BACKEND_URL;

const hasError = (response) => {
  if (!response) return true;
  if (response.httpErrorMessage || response.error) return true;
  return false;
};

const catchErrorData = (error, isBlob, callBack) => {
  const errorData = (error.response && Object.hasOwnProperty.call(error.response, 'data'))
    ? error.response.data
    : null;

  if (callBack) {
    const errorMessage = (errorData && errorData.message) ? errorData.message : '';
    callBack(errorMessage);
  }

  if (isBlob) return error.response;

  return errorData;
};

const sendRequest = (requestObj) => {
  const headers = {};

  const {
    method,
    uri,
    tokenAccess,
    queryParams,
    bodyParams,
    errorCallBack,
    mockResponse,
    isBlob
  } = requestObj;
  headers.Authorization = tokenAccess;

  if (mockResponse) headers['Mock-Response'] = mockResponse;

  let urlQueryParams = '';
  if (queryParams) {
    urlQueryParams = '?';
    Object.keys(queryParams).forEach((key) => {
      urlQueryParams = `${urlQueryParams}${key}=${queryParams[key]}&`;
    });
    urlQueryParams = `${urlQueryParams}?rand=${Math.random()}`;
  }

  let requestBodyParams = null;
  if (bodyParams) {
    requestBodyParams = bodyParams;
  }

  const requestOptions = {
    method,
    url: `${baseUrl}/${uri}${urlQueryParams}`,
    headers,
    data: requestBodyParams,
    params: null,
    json: true
  };

  if (isBlob) {
    requestOptions.responseType = 'blob';
  }

  return axios(requestOptions).then(
    (response) => {
      if (isBlob) {
        return {
          data: response.data,
          headers: response.headers
        };
      }

      return response.data;
    },
    error => catchErrorData(error, isBlob, errorCallBack)
  );
};

const getRefreshRequestData = () => ({
  refreshToken: window.localStorage.getItem('refreshToken') || '',
  tokenCreationDate: window.localStorage.getItem('tokenCreationDate') || '',
  expireTime: window.localStorage.getItem('expireTime') || ''
});

const verifyToken = async (token) => {
  const requestOptions = {
    method: 'GET',
    url: `${baseUrl}/verify?token=${token}`,
    json: true
  };

  try {
    const response = await axios(requestOptions);
    return (response.data) ? response.data : false;
  } catch (error) {
    return false;
  }
};

const getStorageToken = (type) => {
  const token = window.localStorage.getItem(type);
  return (token !== 'undefined' || token !== 'null')
    ? token
    : false;
};

const getRedirectData = async (requestParams) => {
  const storageToken = getStorageToken('tokenAccess');
  const accessToken = requestParams.authToken || storageToken || '';
  const tokenValidated = await verifyToken(accessToken);

  const storageRefreshToken = getStorageToken('refreshToken');
  const refreshToken = requestParams.refreshToken || storageRefreshToken || '';
  const storageExpireTime = getStorageToken('expireTime');
  const expireTime = requestParams.expireTime || storageExpireTime || 500;

  if (!tokenValidated) {
    window.localStorage.removeItem('tokenAccess');
    window.localStorage.removeItem('refreshToken');
    window.localStorage.removeItem('expireTime');
  }

  const viewUrl = window.location.pathname || requestParams.viewName || '';

  const tokenDate = getStorageToken('tokenCreationDate');
  const tokenCreationDate = tokenDate || Date.now();

  return {
    accessToken,
    refreshToken,
    view: viewUrl,
    expireTime,
    invalidToken: (!accessToken || !tokenValidated),
    tokenCreationDate
  };
};

const refreshTokenAccess = (refreshToken) => {
  const uri = `refresh-token?refreshToken=${refreshToken}`;
  return axios(
    {
      method: 'GET',
      url: `${baseUrl}/${uri}`,
      headers: null,
      data: null,
      params: null,
      json: true
    }
  ).then(
    (response) => {
      return response.data;
    },
    (error) => {
      return (error.response && Object.hasOwnProperty.call(error.response, 'data'))
        ? error.response.data
        : null;
    }
  );
};

const getExpireTime = (tokenCreationDate, expireTime) => {
  const currentTime = Date.now();
  if (currentTime <= tokenCreationDate) {
    return expireTime;
  }

  const timeDifference = (currentTime - tokenCreationDate) / 1000;

  return (expireTime > timeDifference) ? (expireTime - timeDifference) : expireTime;
};

const refreshTokenTimeout = (requestData, store) => {
  const intervalSeconds = getExpireTime(requestData.tokenCreationDate, requestData.expireTime);
  const intervalMiliSecons = (intervalSeconds * 0.75) * 1000;

  const timeoutId = window.localStorage.getItem('activeTimeout') || '';
  if (timeoutId) window.clearTimeout(timeoutId);

  const activeTimeOut = setTimeout(
    () => {
      refreshTokenAccess(requestData.refreshToken).then(
        (newToken) => {
          if (hasError(newToken)) {
            window.localStorage.setItem('expireTime', REFRESH_FAILED_TIME_RETRY);
            REFRESH_ATTEMPS_NUMBER -= 1;
            return;
          }

          if (newToken === null) {
            return;
          }

          if (newToken.tokenAccess === null) {
            return;
          }

          if (newToken.tokenRefresh === null) {
            return;
          }

          REFRESH_ATTEMPS_NUMBER = 5;
          store.dispatch({ type: 'REFRESH_TOKEN', newToken });
          window.localStorage.setItem('tokenAccess', newToken.tokenAccess);
          window.localStorage.setItem('refreshToken', newToken.tokenRefresh);
          window.localStorage.setItem('expireTime', newToken.tokenExpiresIn);
          window.localStorage.setItem('tokenCreationDate', Date.now());
        }
      ).then(() => {
        if (REFRESH_ATTEMPS_NUMBER >= 0) refreshTokenTimeout(getRefreshRequestData(), store);
      });
    },
    intervalMiliSecons
  );
  window.localStorage.setItem('activeTimeout', activeTimeOut);
};

const initializeSecurity = async (redirectData, session) => {
  const {
    accessToken, refreshToken, expireTime, tokenCreationDate
  } = redirectData;

  window.localStorage.setItem('tokenAccess', accessToken);
  window.localStorage.setItem('refreshToken', refreshToken);
  window.localStorage.setItem('expireTime', expireTime);
  window.localStorage.setItem('tokenCreationDate', tokenCreationDate);

  refreshTokenTimeout(
    {
      refreshToken,
      tokenCreationDate,
      expireTime
    },
    session
  );
  const crsData = GET_CRS_DATA();
  session.dispatch({ type: 'SET_CRS_DATA', crsData });

  return true;
};

const getHotelRoomDistribution = (
  hotelCode,
  exoCode,
  errorCallBack,
  requestCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'GET',
    uri: `hotels/${hotelCode}/rooms/distribution`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    queryParams: { exoCode },
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      dispatch({
        type: 'SET_HOTEL_ROOMS_LIST',
        response
      });

      requestCallBack();
    }
  );
};

const getAvailableHotelsList = (
  userCode,
  errorCallBack,
  requestCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'GET',
    uri: `users/${userCode}/hotels`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      dispatch({ type: 'GET_ALL_HOTELS_LIST', response });
      requestCallBack();
    }
  );
};

const getAvailableTourOperatorsList = (
  hotelCode,
  errorCallBack,
  requestCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'GET',
    uri: `hotels/${hotelCode}/ttoo`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if(hasError(response)) return;

      dispatch({ type: 'SET_OPERATORS_LIST', response });

      if (requestCallBack) {
        const listLength = response.length;
        const firstOptionOperator = (listLength === 1 && response[0] && response[0].id) ? response[0].id : '';
        requestCallBack(listLength, firstOptionOperator.toString());
      }
    }
  );
};

const getAvailableContractsList = (
  hotelCode,
  tourOperator,
  errorCallBack,
  requestCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'GET',
    uri: `hotels/${hotelCode}/ttoo/${tourOperator}/contracts`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      dispatch({ type: 'SET_CONTRACTS_LIST', response });

      if (response.length === 0) {
        requestCallBack();
      }
    }
  );
};

const loadAndSetDetailContract = (
  reservationId,
  hotelCode,
  tourOperator,
  contractData,
  modified,
  errorCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const { id, rate, period } = contractData;

  const requestObj = {
    method: 'GET',
    uri: `hotels/${hotelCode}/ttoo/${tourOperator}/contracts/${rate.code}`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    queryParams: {
      dateFrom: period.dateFrom,
      dateTo: period.dateTo
    },
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      const contractDetail = {
        ...response,
        id,
        period
      };

      dispatch({
        type: 'UPDATE_CONTRACT_DETAIL_LIST',
        contract: contractDetail
      });

      if (!reservationId || reservationId === '') return;

      dispatch({
        type: 'UPDATE_RESERVATION_CONTRACT',
        id: reservationId,
        rateCode: rate.code,
        contract: contractDetail,
        modified
      });
    }
  )
};

const getTourOperationsReservationsList = (
  hotelCode,
  tourOperator,
  locatorTTOO,
  errorCallBack,
  requestCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'POST',
    uri: 'v2/ttoo/reservations',
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    bodyParams: {
      hotelCode,
      ttooCode: tourOperator,
      ttooLocator: locatorTTOO
    },
    errorCallBack
  };

  const isConfirmed = reservation => (
    reservation.status && reservation.status === STATUS_CONFIRMED && reservation.rateCode !== undefined
  );

  const getConfirmedList = response => response.filter(isConfirmed);

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      requestCallBack();

      if (!response) return;

      const confirmedList = getConfirmedList(response);

      if(confirmedList.length === 0) return;

      dispatch({
        type: 'SET_ALL_RESERVATIONS_LIST',
        reservationsList: confirmedList
      });
    }
  );
};

const getTourOperationsReservationData = (
  reservationId,
  locator,
  tourOperator,
  errorCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'GET',
    uri: `v2/ttoo/${tourOperator}/reservations/${locator}`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      dispatch({
        type: 'SET_FULL_RESERVATION_DATA',
        id: reservationId,
        response
      });
    }
  )
};

const createNewTourOperationsReservation = (
  reservationId,
  tourOperator,
  reservationData,
  errorCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'POST',
    uri: `v2/ttoo/${tourOperator}/reservations`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    bodyParams: { ...reservationData },
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      dispatch({
        type: 'SET_CONFIRMED_RESERVATION',
        id: reservationId,
        response
      });
    }
  )
};

const getTourOperationsReservationAppraise = (
  reservationId,
  tourOperator,
  reservationData,
  errorCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'POST',
    uri: `v2/ttoo/${tourOperator}/reservations/appraise`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    bodyParams: { ...reservationData },
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      dispatch({
        type: 'UPDATE_RESERVATION_APPRAISE',
        id: reservationId,
        response
      });
    }
  )
};

const modifyTourOperationsReservation = (
  reservationId,
  locator,
  tourOperator,
  reservationData,
  errorCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'PUT',
    uri: `v2/ttoo/${tourOperator}/reservations/${locator}`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    bodyParams: { ...reservationData },
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      dispatch({
        type: 'UPDATE_MODIFIED_RESERVATION',
        id: reservationId
      });
    }
  )
};

const cancelTourOperationsReservation = (
  reservationId,
  locator,
  tourOperator,
  errorCallBack,
  requestCallBack
) => (dispatch) => {
  dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: true });

  const requestObj = {
    method: 'DELETE',
    uri: `v2/ttoo/${tourOperator}/reservations/${locator}`,
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      dispatch({ type: 'TOGGLE_LOADING_INDICATOR', isLoading: false });

      if (hasError(response)) return;

      dispatch({
        type: 'ERASE_RESERVATION_FROM_LIST',
        id: reservationId
      });

      requestCallBack();
    }
  )
};

const getGroupsALG = (hotelCode, errorCallBack) => (dispatch) => {
  const requestObj = {
    method: 'GET',
    uri: 'groups/alg',
    tokenAccess: window.localStorage.getItem('tokenAccess'),
    queryParams: { hotelCode },
    errorCallBack
  };

  sendRequest(requestObj).then(
    (response) => {
      if (hasError(response)) return;

      dispatch({
        type: 'SET_GROUPS_LIST',
        groupsList: response
      });
    }
  )
};

export {
  initializeSecurity,
  getRedirectData,
  getAvailableHotelsList,
  getHotelRoomDistribution,
  getAvailableTourOperatorsList,
  getAvailableContractsList,
  loadAndSetDetailContract,
  getTourOperationsReservationsList,
  getTourOperationsReservationData,
  createNewTourOperationsReservation,
  getTourOperationsReservationAppraise,
  modifyTourOperationsReservation,
  cancelTourOperationsReservation,
  getGroupsALG,
  //coverage only
  hasError,
  sendRequest,
  refreshTokenAccess,
  refreshTokenTimeout
};
